import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Check } from "lucide-react";
import { Reveal, MaskedLines, SectionLabel } from "@/components/Reveal";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MS_TASKS } from "@/data/managedServices";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATS = ["Automate Immediately", "AI-Assisted", "Human-Managed", "Requires Further Analysis"];
const CAT_COLORS = { "Automate Immediately": "text-cyan-700 border-cyan-500/50 bg-cyan-500/5", "AI-Assisted": "text-blue-700 border-blue-500/40 bg-blue-500/5", "Human-Managed": "text-zinc-700 border-zinc-900/20 bg-white", "Requires Further Analysis": "text-amber-700 border-amber-500/40 bg-amber-500/5" };
const RANGES = ["1-5", "6-20", "21-50", "51-200", "200+"];

export default function Assessment() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ employees: "", industry: "", monthly_leads: "", inbound_calls: "", outbound_calls: "", crm: "", admin_employees: "", tasks: [], bottleneck: "", desired_outcome: "", name: "", company: "", email: "", phone: "" });

  useEffect(() => { document.title = "AI Operations Opportunity Assessment — Augmented Intelligence Systems"; }, []);

  const toggleTask = (id) => setForm((f) => ({ ...f, tasks: f.tasks.includes(id) ? f.tasks.filter((t) => t !== id) : [...f.tasks, id] }));
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setSel = (k) => (v) => setForm({ ...form, [k]: v });

  const results = CATS.reduce((acc, c) => ({ ...acc, [c]: MS_TASKS.filter((t) => form.tasks.includes(t.id) && t.cat === c).map((t) => t.label) }), {});
  if (["Healthcare", "Finance", "Legal", "Government"].includes(form.industry) && !results["Requires Further Analysis"].length) {
    results["Requires Further Analysis"] = [`${form.industry} processes — compliance requirements evaluated case-by-case`];
  }

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/leads/assessment`, { ...form, results });
      setStep(3);
      toast.success("Assessment received — check your inbox.");
    } catch {
      toast.error("Please check your details and try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full border border-zinc-900/20 bg-white px-4 py-3 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-cyan-500 focus:outline-none";
  const selCls = "h-[46px] rounded-none border-zinc-900/20 bg-white font-normal text-zinc-900 data-[placeholder]:text-zinc-400";

  return (
    <main data-testid="assessment-page" className="grid-bg min-h-screen pt-32 pb-24">
      <div className="mx-auto max-w-4xl px-6">
        <SectionLabel>Free · No Obligation</SectionLabel>
        <MaskedLines className="mt-6" lines={["AI Operations", <span key="a" className="text-cyan-600">Opportunity Assessment</span>]}
          lineClassName="font-heading text-4xl tracking-tighter leading-[1.08] text-zinc-900 sm:text-5xl" />
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-zinc-600">Answer a few questions about how your business runs. We'll show you what to automate immediately, where AI can assist, and what should stay human-managed.</p>

        {step === 1 && (
          <Reveal>
            <div data-testid="assessment-step1" className="mt-12 space-y-8 border border-zinc-900/10 bg-white p-8 sm:p-10">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                {[["employees", "Number of employees"], ["admin_employees", "Administrative employees"], ["monthly_leads", "Monthly leads"], ["inbound_calls", "Monthly inbound calls"], ["outbound_calls", "Monthly outbound calls"]].map(([k, label]) => (
                  <Select key={k} value={form[k]} onValueChange={setSel(k)}>
                    <SelectTrigger data-testid={`assessment-${k}`} className={selCls}><SelectValue placeholder={label} /></SelectTrigger>
                    <SelectContent className="rounded-none border-zinc-900/15 bg-white text-zinc-900">
                      {RANGES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ))}
                <Select value={form.industry} onValueChange={setSel("industry")}>
                  <SelectTrigger data-testid="assessment-industry" className={selCls}><SelectValue placeholder="Industry" /></SelectTrigger>
                  <SelectContent className="rounded-none border-zinc-900/15 bg-white text-zinc-900">
                    {["Professional Services", "Home & Field Services", "Healthcare", "Finance", "Legal", "Government", "Retail / E-commerce", "Technology", "Other"].map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                  </SelectContent>
                </Select>
                <input data-testid="assessment-crm" placeholder="Current CRM / help desk / accounting tools" value={form.crm} onChange={set("crm")} className={`${inputCls} sm:col-span-2`} />
              </div>
              <div>
                <p className="label-mono">Which repetitive tasks eat your team's time?</p>
                <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {MS_TASKS.map((t) => (
                    <button key={t.id} type="button" data-testid={`assessment-task-${t.id}`} onClick={() => toggleTask(t.id)}
                      className={`flex items-center gap-3 border px-4 py-3 text-left text-sm transition-colors ${form.tasks.includes(t.id) ? "border-cyan-500 bg-cyan-500/5 text-zinc-900" : "border-zinc-900/15 text-zinc-600 hover:border-zinc-900/40"}`}>
                      <span className={`flex h-4 w-4 shrink-0 items-center justify-center border ${form.tasks.includes(t.id) ? "border-cyan-500 bg-cyan-500 text-white" : "border-zinc-900/30"}`}>{form.tasks.includes(t.id) && <Check size={11} />}</span>
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
              <input data-testid="assessment-bottleneck" placeholder="Biggest operational bottleneck" value={form.bottleneck} onChange={set("bottleneck")} className={inputCls} />
              <input data-testid="assessment-outcome" placeholder="Desired outcome (e.g., faster response, lower admin cost)" value={form.desired_outcome} onChange={set("desired_outcome")} className={inputCls} />
              <button data-testid="assessment-next-btn" onClick={() => (form.tasks.length ? setStep(2) : toast.error("Select at least one repetitive task."))} className="btn-primary w-full">See My Automation Opportunities <ArrowUpRight size={16} /></button>
            </div>
          </Reveal>
        )}

        {step === 2 && (
          <Reveal>
            <form onSubmit={submit} data-testid="assessment-step2" className="mt-12 border border-cyan-500/40 bg-white p-8 sm:p-10">
              <p className="label-mono">One last step</p>
              <h2 className="mt-3 font-heading text-2xl text-zinc-900">Where should we send your detailed assessment?</h2>
              <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
                <input data-testid="assessment-name" required placeholder="Full name *" value={form.name} onChange={set("name")} className={inputCls} />
                <input data-testid="assessment-company" required placeholder="Company *" value={form.company} onChange={set("company")} className={inputCls} />
                <input data-testid="assessment-email" required type="email" placeholder="Work email *" value={form.email} onChange={set("email")} className={inputCls} />
                <input data-testid="assessment-phone" placeholder="Phone" value={form.phone} onChange={set("phone")} className={inputCls} />
              </div>
              <button data-testid="assessment-submit-btn" type="submit" disabled={loading} className="btn-primary mt-8 w-full">{loading ? "Analyzing…" : <>Get My Assessment <ArrowUpRight size={16} /></>}</button>
            </form>
          </Reveal>
        )}

        {step === 3 && (
          <div data-testid="assessment-results" className="mt-12">
            <div className="border border-cyan-500/40 bg-cyan-500/5 p-8">
              <Check size={24} className="text-cyan-600" />
              <h2 className="mt-3 font-heading text-2xl text-zinc-900">Potential AI Automation Opportunities</h2>
              <p className="mt-2 text-sm text-zinc-600">Initial assessment for {form.company}. A specialist will follow up with a detailed analysis — confirmation sent to {form.email}.</p>
            </div>
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {CATS.map((c) => (
                <div key={c} data-testid={`assessment-cat-${c.replace(/\s/g, "-").toLowerCase()}`} className={`border p-6 ${CAT_COLORS[c]}`}>
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em]">{c}</p>
                  {results[c].length ? (
                    <ul className="mt-3 space-y-1.5">{results[c].map((r) => <li key={r} className="text-sm text-zinc-700">— {r}</li>)}</ul>
                  ) : <p className="mt-3 text-sm text-zinc-400">Nothing identified in this category yet.</p>}
                </div>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href="/contact" className="btn-primary">Schedule My Free Strategy Session <ArrowUpRight size={16} /></a>
              <a href="/managed-services" className="btn-secondary">Explore Managed Services</a>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
